// analytics.js — centralised GA4 event tracking via Firebase Analytics
// All tracking calls go through here so they're easy to find and modify.
// GA4 must be enabled in Firebase console for events to appear in dashboard.

let analyticsInstance = null;

const getAnalytics = async () => {
  if (analyticsInstance) return analyticsInstance;
  try {
    const { getAnalytics: initAnalytics, isSupported } = await import('firebase/analytics');
    const { default: app } = await import('./firebase');
    const supported = await isSupported();
    if (!supported) return null;
    analyticsInstance = initAnalytics(app);
    return analyticsInstance;
  } catch (err) {
    console.warn('Analytics unavailable:', err.message);
    return null;
  }
};

const track = async (eventName, params = {}) => {
  try {
    const analytics = await getAnalytics();
    if (!analytics) return;
    const { logEvent } = await import('firebase/analytics');
    logEvent(analytics, eventName, params);
  } catch (err) {
    // Never let analytics errors affect the user
    console.warn('Analytics error:', err.message);
  }
};

// ── Auth ──────────────────────────────────────────────────────────────────────

export const trackSignup = (method = 'email') =>
  track('sign_up', { method });

export const trackLogin = (method = 'email') =>
  track('login', { method });

// ── Uploads ───────────────────────────────────────────────────────────────────

export const trackFileUpload = ({ fileType, totalSnps, analyzedSnps, savedToCloud }) =>
  track('file_upload', {
    file_type: fileType,           // '23andme' | 'ancestry' | 'unknown'
    total_snps: totalSnps,
    analyzed_snps: analyzedSnps,
    saved_to_cloud: savedToCloud,
  });

export const trackDemoLoad = (demoType, fromCache) =>
  track('demo_load', {
    demo_type: demoType,           // '23andme' | 'ancestry'
    from_cache: fromCache,
  });

// ── AI Assistant ──────────────────────────────────────────────────────────────

export const trackAiMessage = ({ usedLookup, usedWebSearch, contextFileCount }) =>
  track('ai_message_sent', {
    used_lookup_tool: usedLookup,
    used_web_search: usedWebSearch,
    context_file_count: contextFileCount,
  });

export const trackAiConversationStart = () =>
  track('ai_conversation_start');

// ── Feature usage ─────────────────────────────────────────────────────────────

export const trackResultsViewed = ({ totalSnps, plan }) =>
  track('results_viewed', {
    snp_count: totalSnps,
    plan,
  });